import React, { useState } from 'react'

export interface ToggleButtonProps {
    variant?: 'default' | 'ghost'
    size?: 'sm' | 'md' | 'lg'
    selected?: boolean
    defaultSelected?: boolean
    onSelectedChange?: (selected: boolean) => void
    disabled?: boolean
    iconOnly?: boolean
    label?: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    style?: React.CSSProperties
}

const sizeConfig = {
    sm: { base: 'h-7 rounded-lg text-xs leading-[1.34]', padding: 'px-2.5 py-1', iconPadding: 'px-1.5 py-1' },
    md: { base: 'h-9 rounded-3xl text-sm leading-[1.43]', padding: 'px-4 py-2', iconPadding: 'px-2.5 py-2' },
    lg: { base: 'h-11 rounded-3xl text-base leading-[1.5]', padding: 'px-5 py-2.5', iconPadding: 'px-3 py-2.5' },
}

export const ToggleButton = ({
    variant = 'default',
    size = 'md',
    selected: controlledSelected,
    defaultSelected = false,
    onSelectedChange,
    disabled = false,
    iconOnly = false,
    label,
    prefix,
    suffix,
    style,
}: ToggleButtonProps) => {
    const [internalSelected, setInternalSelected] = useState(defaultSelected)

    const selected = controlledSelected !== undefined ? controlledSelected : internalSelected
    const config = sizeConfig[size]
    const isGhost = variant === 'ghost'

    const handleClick = () => {
        if (disabled) return
        const next = !selected
        if (controlledSelected === undefined) setInternalSelected(next)
        onSelectedChange?.(next)
    }

    const stateClasses = selected
        ? 'bg-accent text-accent-foreground enabled:hover:bg-accent-hover enabled:active:bg-accent-hover'
        : isGhost
          ? 'bg-transparent text-foreground enabled:hover:bg-default enabled:active:bg-default'
          : 'bg-default text-foreground enabled:hover:bg-default-hover enabled:active:bg-default-hover'

    return (
        <button
            type="button"
            role="switch"
            aria-checked={selected}
            disabled={disabled}
            onClick={handleClick}
            className={`box-border inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap border-none font-medium outline-none transition-[background,box-shadow] duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background
                disabled:cursor-not-allowed disabled:opacity-50
                ${config.base} ${iconOnly ? config.iconPadding : config.padding} ${stateClasses}`}
            style={style}
        >
            {prefix && <span className="flex shrink-0 items-center">{prefix}</span>}
            {!iconOnly && label && <span>{label}</span>}
            {suffix && <span className="flex shrink-0 items-center">{suffix}</span>}
        </button>
    )
}
